import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import {
    useGetPricingPlan
} from 'src/features/pricingPlan/api/useGetPricingPlan';
import FormattedTimeStat from 'src/features/pricingPlan/components/FormattedTimeStat';
import PreviousNextVersionLink from 'src/features/pricingPlan/components/PreviousNextVersionLink';
import PricingPlanBadge from 'src/features/pricingPlan/components/PricingPlanBadge';
import PricingPlanDashboardGeneralBlock from 'src/features/pricingPlan/components/PricingPlanDashboardGeneralBlock';
import PricingPlanDashboardTables from 'src/features/pricingPlan/components/PricingPlanDashboardTables';
import VersionStatusBadge from 'src/features/pricingPlan/components/VersionStatusBadge';
import { CALCULATION_TYPE, PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import {
    cappingTranslationMapping,
    freeTimeTypeMapping,
    pricingBillingBadgeOptions,
    pricingCalculationBadgeOptions,
    pricingStatusBadgeOptions,
    tickTypeTranslationMapping,
} from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { commonMessages } from 'src/utils/messages';
import { cn } from 'src/utils/shadcn';
import PublishPlanModal from 'src/features/pricingPlan/components/PublishPlanModal';
import { useParking } from 'src/providers/ParkingProvider';
import UpdatePricingPlanFormModal from 'src/features/pricingPlan/components/form/UpdatePricingPlanFormModal';
import CreatePricingPlanVersionFormModal
    from 'src/features/pricingPlan/components/form/CreatePricingPlanVersionFormModal';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const PricingPlanDashboard = () => {
    const { formatMessage, locale } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(pricingPlanMessages.pricingPlanDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: pricingPlanData, isLoading, isError } = useGetPricingPlan({
        planId,
        parkingId,
        config: { enabled: !!planId },
    });

    if (isLoading || isError) return null;

    const isPerFixedTick = pricingPlanData?.calculation_type === CALCULATION_TYPE.PER_FIXED_TICK;

    const isPerFixedPrice = pricingPlanData?.calculation_type === CALCULATION_TYPE.FIXED_PRICE;

    const pricingPlanName =
        pricingPlanData?.translations?.find((plan) => plan.language === locale)?.name ??
        formatMessage(pricingPlanMessages.pricingPlan);

    const fixedRate = pricingPlanData?.fixed_rate
        ? (Number(pricingPlanData.fixed_rate.rate_with_tax)).toFixed(2)
        : '-';

    const taxPercent = pricingPlanData?.tax_percent
        ? `${(Number(pricingPlanData?.tax_percent) * 100).toFixed(2)}%`
        : '-';

    const freeTimeTypeDisplay = pricingPlanData?.free_time_type
        ? formatMessage(freeTimeTypeMapping[pricingPlanData?.free_time_type])
        : '-';

    const cappingDisplay = pricingPlanData?.capping
        ? formatMessage(cappingTranslationMapping[pricingPlanData?.capping])
        : '-';

    const tickTypeDisplay = pricingPlanData?.tick?.tick_type
        ? formatMessage(tickTypeTranslationMapping[pricingPlanData?.tick?.tick_type])
        : '-';

    const dashboardBlockData = {
        section1: [
            {
                translationTitle: pricingPlanMessages.billingType,
                dataField: (
                    <PricingPlanBadge
                        badgeOptions={pricingBillingBadgeOptions}
                        badgeIndexKey={pricingPlanData?.billing_type}
                    />
                ),
            },
            {
                translationTitle: pricingPlanMessages.calculationType,
                dataField: (
                    <PricingPlanBadge
                        badgeOptions={pricingCalculationBadgeOptions}
                        badgeIndexKey={pricingPlanData?.calculation_type}
                    />
                ),
            },
            ...(isPerFixedTick
                ? [
                    {
                        translationTitle: pricingPlanMessages.tickType,
                        dataField: tickTypeDisplay
                    },
                    {
                        translationTitle: pricingPlanMessages.tickDuration,
                        dataField: <FormattedTimeStat totalMinutes={pricingPlanData?.tick?.tick_minutes} />,
                    },
                ]
                : isPerFixedPrice ? [{
                    translationTitle: pricingPlanMessages.price,
                    dataField: fixedRate
                }] : [{
                    translationTitle: pricingPlanMessages.capping,
                    dataField: cappingDisplay
                }]),
            {
                translationTitle: pricingPlanMessages.status,
                dataField: (
                    <PricingPlanBadge
                        badgeOptions={pricingStatusBadgeOptions}
                        badgeIndexKey={pricingPlanData?.status}
                    />
                ),
            },
        ],
        section2: [
            {
                translationTitle: commonMessages.currency,
                dataField: pricingPlanData?.currency,
            },
            {
                translationTitle: pricingPlanMessages.taxPercent,
                dataField: taxPercent
            },
        ],
        section3: [
            {
                translationTitle: pricingPlanMessages.freeTimeType,
                dataField: freeTimeTypeDisplay,
            },
            {
                translationTitle: pricingPlanMessages.freeTime,
                dataField: <FormattedTimeStat totalMinutes={pricingPlanData?.free_time_minutes} />,
            },
            {
                translationTitle: pricingPlanMessages.freeTimeCap,
                dataField: <FormattedTimeStat totalMinutes={pricingPlanData?.free_time_cap_minutes} />,
            },
            {
                translationTitle: pricingPlanMessages.exitTime,
                dataField: <FormattedTimeStat totalMinutes={pricingPlanData?.exit_time} />,
            },
            {
                translationTitle: pricingPlanMessages.exitTimeGrace,
                dataField: <FormattedTimeStat totalMinutes={pricingPlanData?.exit_time_grace} />,
            },
        ],
        section4: [
            {
                translationTitle: pricingPlanMessages.validFromPricing,
                dataField: getFormattedDateTimeForDetailPanel(pricingPlanData?.valid_from),
            },
            {
                translationTitle: pricingPlanMessages.validToPricing,
                dataField: getFormattedDateTimeForDetailPanel(pricingPlanData?.valid_to),
            },
            {
                translationTitle: pricingPlanMessages.previousVersion,
                dataField: <PreviousNextVersionLink version={pricingPlanData?.previous_version} />,
            },
            {
                translationTitle: pricingPlanMessages.nextVersion,
                dataField: <PreviousNextVersionLink version={pricingPlanData?.next_version} />,
            },
        ],
        section5: [
            {
                translationTitle: pricingPlanMessages.description,
                dataField: pricingPlanData?.translations?.find((des) => des.language === locale)?.description ?? '-',
            },
        ],
    };

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/${PATH.ADMINISTRATION}/pricing-plan`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(pricingPlanMessages.backPricingPlans)}
                </Link>
            </Button>
            <SectionBlock>
                <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                    <SectionBlockHead>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{pricingPlanName}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            <VersionStatusBadge
                                is_current_version={pricingPlanData?.is_current_version}
                                validFrom={pricingPlanData?.valid_from}
                            />
                        </SectionBlockSubtitle>
                    </SectionBlockHead>

                    <div className="flex flex-row gap-2">
                        <Button asChild size="mdIcon" variant="outline">
                            <Link
                                to={`/${PATH.ADMINISTRATION}/pricing-plan/${pricingPlanData?.id}/${pricingPlanData?.identity}`}
                            >
                                <div className="block md:hidden">
                                    <FontAwesomeIcon icon={ICON_MAP.TABLE_LIST} className="opacity-50" />
                                </div>
                                <span className="hidden md:block">
                                    {formatMessage(pricingPlanMessages.allVersions)}
                                </span>
                            </Link>
                        </Button>

                        {managePricingPlans && (
                            <>
                                <UpdatePricingPlanFormModal
                                    selectedPricingPlan={pricingPlanData}
                                    semantics={{
                                        entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdatePricingPlanFormModal>

                                <CreatePricingPlanVersionFormModal
                                    title={formatMessage(pricingPlanMessages.newVersion)}
                                />
                            </>
                        )}

                        {managePricingPlans && pricingPlanData && pricingPlanData.status === PRICING_PLAN_STATUS.DRAFT && (
                            <PublishPlanModal plan={pricingPlanData}>
                                <Button size="mdIcon" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.PUBLISH} />
                                    <span className="hidden md:block">
                                        {formatMessage(pricingPlanMessages.publish)}
                                    </span>
                                </Button>
                            </PublishPlanModal>
                        )}
                    </div>
                </SectionBlockHeader>

                <GeneralBlock asChild>
                    <SectionBlockBody>
                        {Object.entries(dashboardBlockData).map(([sectionName, items], index) => (
                            <React.Fragment key={sectionName}>
                                <div
                                    key={sectionName}
                                    className={cn(
                                        'xs:grid-cols-1 relative grid h-full gap-4 overflow-hidden md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
                                        sectionName === 'section5' && 'flex flex-row flex-wrap',
                                    )}
                                >
                                    {items.map(({ translationTitle, dataField }) => {
                                        return (
                                            <PricingPlanDashboardGeneralBlock
                                                translationTitle={translationTitle}
                                                dataField={dataField}
                                                isLoading={false}
                                                key={translationTitle.id}
                                            />
                                        );
                                    })}
                                </div>
                                {index !== 4 && <SectionBlockSeparator />}
                            </React.Fragment>
                        ))}
                    </SectionBlockBody>
                </GeneralBlock>
            </SectionBlock>

            <PricingPlanDashboardTables
                isPerFixedTick={isPerFixedTick}
                currency={pricingPlanData?.currency}
                pricingPlanIdentity={pricingPlanData?.identity}
            />
        </>
    );
};

export default PricingPlanDashboard;
